/**
 * MIGRATION:phase-8-active-context expires:2027-01-31
 * Reason: Legacy account-type helpers. Replaced by ActiveContext.
 * Delete when: Admin dashboard finishes migrating off `account_type`
 *              AND a product decision is made on whether ActiveContext
 *              is still justified now that only 'personal' | 'admin' scopes remain.
 * Owner: @engineering
 *
 * Use the ActiveContext system instead:
 * - Import: `import { useActiveContext } from '@/contexts/ActiveContextProvider'`
 * - Hook: `const { activeContext } = useActiveContext()`
 * - Scope: activeContext.scope ('personal' | 'admin')
 *
 * @see app/src/contexts/ActiveContextProvider.tsx
 * @see app/src/hooks/useActiveContextCompat.ts
 * @see /docs/MIGRATIONS.md
 */

/**
 * @deprecated Phase 8 - Use ActiveContext scopes instead
 * Replace with: 'personal' | 'admin'
 */
export type AccountType = 'individual' | 'admin'

/**
 * @deprecated Phase 8 - Query membership tables directly
 */
export interface AccountTypeData {
  is_admin: boolean | null
  account_type: 'individual'
}

/**
 * @deprecated Phase 8 - Use useActiveContext() hook instead
 *
 * Get effective account type from account data.
 *
 * @param accountData - Account data from AuthContext
 * @returns AccountType enum
 *
 * Migration: Replace with activeContext.scope
 */
export function getEffectiveAccountType(accountData: AccountTypeData | null): AccountType {
  if (!accountData) {
    return 'individual'
  }

  // Priority 1: Admin (highest privilege)
  if (accountData.is_admin === true) {
    return 'admin'
  }

  return 'individual'
}

/**
 * @deprecated Phase 8 - Use activeContext.scope === 'target' instead
 *
 * Check if account has specific effective type
 *
 * @param accountData - Account data from AuthContext
 * @param targetType - Type to check for
 * @returns boolean
 */
export function isAccountType(
  accountData: AccountTypeData | null,
  targetType: AccountType,
): boolean {
  return getEffectiveAccountType(accountData) === targetType
}

/**
 * @deprecated Phase 8 - Use activeContext.scope comparison instead
 *
 * Check if account is any of the specified types
 *
 * @param accountData - Account data from AuthContext
 * @param targetTypes - Array of types to check
 * @returns boolean
 */
export function isAnyAccountType(
  accountData: AccountTypeData | null,
  targetTypes: AccountType[],
): boolean {
  const effectiveType = getEffectiveAccountType(accountData)
  return targetTypes.includes(effectiveType)
}

/**
 * @deprecated Phase 8 - Use context-based display labels instead
 *
 * Get human-readable account type label
 *
 * @param accountType - AccountType enum
 * @returns Display label
 */
export function getAccountTypeLabel(accountType: AccountType): string {
  switch (accountType) {
    case 'admin':
      return 'Administrator'
    case 'individual':
      return 'Individual User'
    default:
      return 'individual'
  }
}

/**
 * @deprecated Phase 8 - Use context-based descriptions instead
 *
 * Get account type description
 *
 * @param accountType - AccountType enum
 * @returns Description text
 */
export function getAccountTypeDescription(accountType: AccountType): string {
  switch (accountType) {
    case 'admin':
      return 'Full system access with administrative privileges'
    case 'individual':
      return 'Personal account with individual settings'
    default:
      return ''
  }
}

export function hasAutonomousRiskSettings(accountType: AccountType): boolean {
  return ['individual', 'admin'].includes(accountType)
}
